import type { CustomerFormErrors, CustomerFormValues } from './form';

/**
 * Client-side form validation.
 *
 * Mirrors the API rules so a mistake is caught before a round trip — it is a usability
 * affordance, not a security boundary. The server validates every field again, and its answer is
 * the one that counts.
 */

/** Letters from any script plus the punctuation names use. Matches the API's NAME_PATTERN. */
const NAME_PATTERN = /^[\p{L}\p{M}\p{Zs}'’\-.]+$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS = /\d/u;
const MAX_AGE_YEARS = 120;

export function validateCustomerForm(
  values: CustomerFormValues,
  t: (key: string) => string,
): CustomerFormErrors {
  const errors: CustomerFormErrors = {};

  const name = values.fullName.trim();
  if (name.length < 2) {
    errors.fullName = t('customers.validation.nameRequired');
  } else if (name.length > 200) {
    errors.fullName = t('customers.validation.nameTooLong');
  } else if (!NAME_PATTERN.test(name)) {
    errors.fullName = t('customers.validation.nameInvalid');
  }

  const mobile = values.mobile.trim();
  if (mobile.length === 0) {
    errors.mobile = t('customers.validation.mobileRequired');
  } else if (!DIGITS.test(mobile) || mobile.replace(/\D/g, '').length < 6) {
    errors.mobile = t('customers.validation.mobileInvalid');
  }

  if (values.alternateMobile.trim() && values.alternateMobile.replace(/\D/g, '').length < 6) {
    errors.alternateMobile = t('customers.validation.mobileInvalid');
  }

  if (values.email.trim() && !EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = t('customers.validation.emailInvalid');
  }

  if (values.dateOfBirth) {
    const date = new Date(values.dateOfBirth);
    const now = new Date();
    const oldest = new Date(now.getFullYear() - MAX_AGE_YEARS, now.getMonth(), now.getDate());

    if (Number.isNaN(date.getTime())) {
      errors.dateOfBirth = t('customers.validation.dateInvalid');
    } else if (date > now) {
      errors.dateOfBirth = t('customers.validation.dateFuture');
    } else if (date < oldest) {
      errors.dateOfBirth = t('customers.validation.dateTooOld');
    }
  }

  return errors;
}

export function hasErrors(errors: CustomerFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Drops blank optional fields so the API receives absent values rather than empty strings. */
export function toCustomerPayload(values: CustomerFormValues): Record<string, string> {
  const payload: Record<string, string> = {
    fullName: values.fullName.trim(),
    mobile: values.mobile.trim(),
    preferredLanguage: values.preferredLanguage,
  };

  const optional: [keyof CustomerFormValues, string][] = [
    ['alternateMobile', 'alternateMobile'],
    ['email', 'email'],
    ['dateOfBirth', 'dateOfBirth'],
    ['gender', 'gender'],
    ['notes', 'notes'],
  ];

  for (const [field, key] of optional) {
    const value = values[field]?.trim();
    if (value) payload[key] = value;
  }

  return payload;
}
